import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorldMapData } from '../../types';
import { statsApi } from '../../api/stats.api';
import { countryName } from '../../utils/countries';

/**
 * ADR-0045: the world with the visited countries painted. The geometry comes
 * from the server on demand — never import it here: the main chunk sits at the
 * PWA's 4 MiB limit.
 */
export default function WorldMap({ visited }: { visited: string[] }) {
  const { t, i18n } = useTranslation();
  const [map, setMap] = useState<WorldMapData | null>(null);

  useEffect(() => {
    statsApi.worldMap().then(setMap).catch(() => setMap(null));
  }, []);

  if (!map) return <div className="aspect-[2/1] rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />;

  const on = new Set(visited);
  return (
    <svg
      viewBox={`0 0 ${map.width} ${map.height}`}
      className="w-full h-auto"
      role="img"
      aria-label={t('stats.mapLabel', { count: visited.length })}
    >
      {Object.entries(map.countries).map(([code, d]) => (
        <path
          key={code}
          d={d}
          className={on.has(code)
            ? 'fill-teal-500 stroke-white dark:stroke-slate-900'
            : 'fill-slate-200 dark:fill-slate-700 stroke-white dark:stroke-slate-900'}
          strokeWidth={0.5}
        >
          <title>{countryName(code, i18n.language)}</title>
        </path>
      ))}
    </svg>
  );
}
